/**
 * One-time "rate our app" modal (specs/review-prompt), design-ported from the
 * Claude Design "Rating Modal" handoff. Invitation only: the OS review sheet
 * (or store page) is the actual rating UI. Accept (any star / CTA) → onAccept,
 * then a neutral thanks line and self-close; X → onClose('dismissed').
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Image,
  Pressable,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { ChevronRight, Trophy, X } from 'lucide-react-native';
import { BaseModal } from '../../../../components/ui/BaseModal/BaseModal';
import { getAssetUrl, ASSET_PATHS } from '../../../../constants/assets';
import { ReviewPromptMascotStage } from './ReviewPromptMascotStage';
import { ReviewPromptStars } from './ReviewPromptStars';
import { getReviewPromptModalStyles } from './ReviewPromptModal.styles';
import {
  REVIEW_PROMPT_COLORS,
  REVIEW_PROMPT_COPY,
  REVIEW_PROMPT_LAYOUT,
  REVIEW_PROMPT_TIMING,
} from './reviewPromptModal.constants';

const CTA_COIN_URI = getAssetUrl(ASSET_PATHS.COINS.COIN_IDLE);

export type ReviewPromptCloseReason = 'accepted' | 'dismissed';

export interface ReviewPromptModalProps {
  visible: boolean;
  /** Kick off the store-review handoff; awaited before the thanks line shows. */
  onAccept: () => Promise<void> | void;
  onClose: (reason: ReviewPromptCloseReason) => void;
}

export const ReviewPromptModal: React.FC<ReviewPromptModalProps> = ({
  visible,
  onAccept,
  onClose,
}) => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  const { width: windowWidth } = useWindowDimensions();
  const cardWidth = Math.min(REVIEW_PROMPT_LAYOUT.MAX_CARD_WIDTH, windowWidth - 32);

  const [isThanksVisible, setIsThanksVisible] = useState(false);
  const isAcceptingRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cardIn = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    setIsThanksVisible(false);
    isAcceptingRef.current = false;
    cardIn.setValue(0);
    const entrance = Animated.timing(cardIn, {
      toValue: 1,
      duration: REVIEW_PROMPT_TIMING.CARD_IN_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    const pulseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: REVIEW_PROMPT_TIMING.CTA_PULSE_MS,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, { toValue: 0, duration: 0, useNativeDriver: true }),
      ]),
    );
    entrance.start();
    pulseLoop.start();
    return () => {
      entrance.stop();
      pulseLoop.stop();
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [visible, cardIn, pulse]);

  const handleDismiss = useCallback(() => {
    if (isAcceptingRef.current) return;
    onClose('dismissed');
  }, [onClose]);

  const handleAccept = useCallback(async () => {
    if (isAcceptingRef.current) return;
    isAcceptingRef.current = true;
    try {
      await onAccept();
    } finally {
      setIsThanksVisible(true);
      closeTimerRef.current = setTimeout(
        () => onClose('accepted'),
        REVIEW_PROMPT_TIMING.TOAST_CLOSE_DELAY_MS,
      );
    }
  }, [onAccept, onClose]);

  const cardTranslateY = cardIn.interpolate({ inputRange: [0, 1], outputRange: [40, 0] });
  const cardScale = cardIn.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] });
  const ringScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.1] });
  const ringOpacity = pulse.interpolate({ inputRange: [0, 0.6, 1], outputRange: [0.55, 0.15, 0] });

  return (
    <BaseModal
      isVisible={visible}
      onDismiss={handleDismiss}
      dismissible={false}
      testID="review-prompt-modal"
      accessibilityLabel={REVIEW_PROMPT_COPY.TITLE}
    >
      <Animated.View
        style={[
          styles.cardWrap,
          { width: cardWidth, opacity: cardIn, transform: [{ translateY: cardTranslateY }, { scale: cardScale }] },
        ]}
      >
        <View style={styles.card}>
          <LinearGradient
            colors={[REVIEW_PROMPT_COLORS.CARD_TOP, REVIEW_PROMPT_COLORS.CARD_BOTTOM]}
            style={styles.cardGradient}
            pointerEvents="none"
          />

          <View style={styles.trophyBadge} pointerEvents="none">
            <Trophy size={28} color={REVIEW_PROMPT_COLORS.GOLD} fill={REVIEW_PROMPT_COLORS.GOLD} />
          </View>

          <Pressable
            onPress={handleDismiss}
            style={({ pressed }) => [styles.closeButton, pressed && styles.closeButtonPressed]}
            accessibilityRole="button"
            accessibilityLabel={REVIEW_PROMPT_COPY.A11Y_CLOSE}
            testID="review-prompt-close"
          >
            <X size={18} color={REVIEW_PROMPT_COLORS.CLOSE_ICON} strokeWidth={2.5} />
          </Pressable>

          <Text style={styles.title}>{REVIEW_PROMPT_COPY.TITLE}</Text>
          <Text style={styles.subtitle}>
            {REVIEW_PROMPT_COPY.SUBTITLE_PREFIX}
            <Text style={styles.subtitleHighlight}>{REVIEW_PROMPT_COPY.SUBTITLE_HIGHLIGHT}</Text>
            {REVIEW_PROMPT_COPY.SUBTITLE_SUFFIX}
          </Text>

          <ReviewPromptMascotStage />

          <ReviewPromptStars onPress={handleAccept} disabled={isThanksVisible} />

          <Text style={styles.goldLine}>{REVIEW_PROMPT_COPY.GOLD_LINE}</Text>
          <Text style={styles.subLine}>{REVIEW_PROMPT_COPY.SUB_LINE}</Text>

          <View style={styles.ctaWrap}>
            <Animated.View
              pointerEvents="none"
              style={[styles.ctaPulseRing, { opacity: ringOpacity, transform: [{ scale: ringScale }] }]}
            />
            <Pressable
              onPress={handleAccept}
              disabled={isThanksVisible}
              style={({ pressed }) => [styles.ctaButton, pressed && styles.ctaButtonPressed]}
              accessibilityRole="button"
              accessibilityLabel={REVIEW_PROMPT_COPY.CTA}
              testID="review-prompt-cta"
            >
              <LinearGradient
                colors={[
                  REVIEW_PROMPT_COLORS.CTA_TOP,
                  REVIEW_PROMPT_COLORS.CTA_MID,
                  REVIEW_PROMPT_COLORS.CTA_BOTTOM,
                ]}
                style={styles.ctaGradient}
                pointerEvents="none"
              />
              <View style={styles.ctaContent}>
                <Image source={{ uri: CTA_COIN_URI }} style={styles.ctaCoin} resizeMode="contain" />
                <Text style={styles.ctaLabel}>{REVIEW_PROMPT_COPY.CTA}</Text>
                <ChevronRight size={20} color={REVIEW_PROMPT_COLORS.CTA_TEXT} strokeWidth={3} />
              </View>
            </Pressable>
          </View>

          {isThanksVisible && <Text style={styles.toast}>{REVIEW_PROMPT_COPY.TOAST}</Text>}
        </View>
      </Animated.View>
    </BaseModal>
  );
};
